/*
 * Northside - size picker in the product card (snippet northside-card-atc),
 * AlleyOop style: "Elige tu talla" unfolds the sizes under the button; tapping
 * a size adds that variant to the cart right away, the size stays marked and
 * the panel stays open (to add another size). "Cerrar" or Escape folds it.
 *
 * The add goes through the Cart API and announces itself with the same
 * standard event the native product forms use (CartLinesUpdateEvent, action
 * "add"), so the mini cart opens and every cart listener stays in sync.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const T = {
  adding: 'Agregando al carrito...',
  added: 'agregada al carrito',
  error: 'No pudimos agregarla. Intenta de nuevo.',
};

async function getCart() {
  const res = await fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  return res.json();
}

class NsCardAtc extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-ns-card-atc]');
    this.panel = this.querySelector('[data-ns-card-sizes]');
    this.live = this.querySelector('[data-ns-sizes-msg]');
    this.errorBox = this.querySelector('[data-ns-sizes-error]');
    this.mode = this.dataset.mode;
    this.card = this.closest('product-card, .product-card');
    if (this.card) this.card.classList.add('ns-has-card-atc', 'ns-card-host');
    if (!this.button || this.mode === 'popup') return; // native quick add (northside.js)

    this.button.addEventListener('click', this.onButton);
    if (this.panel) {
      this.panel.addEventListener('click', this.onPanel);
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.panel.hidden) this.close(true);
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('ns:card-sizes-open', this.onOtherOpen);
  }

  onButton = (event) => {
    event.preventDefault();
    if (this.mode === 'single') {
      this.add(this.dataset.variantId, this.button);
    } else if (this.panel) {
      this.panel.hidden ? this.open() : this.close(false);
    }
  };

  onPanel = (event) => {
    if (event.target.closest('[data-ns-sizes-close]')) {
      event.preventDefault();
      this.close(true);
      return;
    }
    const size = event.target.closest('[data-ns-size]');
    if (size && !size.disabled) {
      event.preventDefault();
      this.add(size.dataset.variantId, size);
    }
  };

  /* Only one card open at a time */
  onOtherOpen = (event) => {
    if (event.detail !== this) this.close(false);
  };

  open() {
    document.dispatchEvent(new CustomEvent('ns:card-sizes-open', { detail: this }));
    this.panel.hidden = false;
    this.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    this.showError('');
    document.addEventListener('ns:card-sizes-open', this.onOtherOpen);
  }

  close(returnFocus) {
    if (!this.panel || this.panel.hidden) return;
    this.panel.hidden = true;
    this.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('ns:card-sizes-open', this.onOtherOpen);
    if (returnFocus) this.button.focus({ preventScroll: true });
  }

  say(text) {
    if (this.live) this.live.textContent = text;
  }

  showError(text) {
    if (!this.errorBox) return;
    this.errorBox.textContent = text;
    this.errorBox.hidden = !text;
  }

  async add(variantId, source) {
    if (this.busy || !variantId) return;
    this.busy = true;
    this.showError('');
    source.classList.add('is-loading');
    this.say(T.adding);

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.error);
      const cart = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: 1, source: 'ns-card-atc', productId: this.dataset.productId, didError: false },
      });
      source.classList.remove('is-loading');
      if (source.hasAttribute('data-ns-size')) {
        this.panel.querySelectorAll('[data-ns-size]').forEach((b) => b.classList.toggle('is-selected', b === source));
        source.classList.add('is-added');
        setTimeout(() => source.classList.remove('is-added'), 900);
        this.say((source.textContent || '').trim() + ' ' + T.added);
      } else {
        this.say(T.added);
      }
    } catch (err) {
      source.classList.remove('is-loading');
      this.showError((err && err.message) || T.error);
      this.say((err && err.message) || T.error);
      getCart()
        .then((cart) =>
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: 0, source: 'ns-card-atc', didError: true },
          })
        )
        .catch(() => deferred.reject(err));
    } finally {
      this.busy = false;
    }
  }
}

if (!customElements.get('ns-card-atc')) customElements.define('ns-card-atc', NsCardAtc);
